import * as React from 'react'
import { Button, Callout, Collapse, Intent, Spinner } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash.isempty'
import { UprightStyle } from '../../../../../types'
import { MarketplaceServiceSpecific } from '../types'
import { pluralizeFromNumber } from '../../../../../utils/string'
import { SpecificInput, SpecificsFreeTextInput } from './SpecificInput'
import { GoodwillfindsListing } from '../../../../../types/api/listings'
import Client from '../../../../../client'
import { getErrorString } from '../../../../../utils/serverErrorUtils'
import { useGoodwillfindsChannelFormContext } from '../../../../products_v4/listings/goodwillfinds/GoodwillfindsChannelFormContext'

import './specificsStyle.scss'
import { getAutofilledSpecifics } from '../utils'
import useAPI from '../../../../../hooks/useAPI'

interface Props {
  form: GoodwillfindsListing
  onFormChange: ({ name, value }: { name: string; value: object }) => void
  sectionOpen?: boolean
}

const specificsStyles: UprightStyle = {
  loadingSpinner: { textAlign: 'left' },
  topPadding: { paddingTop: 10 }
}

export const Specifics: FC<Props> = _props => {
  const {
    form,
    onListingChange,
    localErrors,
    setLocalErrors,
    templateForm,
    goodwillfindsRequirements,
    setGoodwillfindsRequirements
  } = useGoodwillfindsChannelFormContext()

  const [nonRequiredSpecificsOpen, setNonRequiredSpecificsOpen] = React.useState(false)
  const [specificsLookup, setSpecificsLookup] = useState({})

  const fetchSpecifics = useCallback(
    () => Client.goodwillFindsCategorySpecifics(form.categoryMarketCategoryId),
    [form.categoryMarketCategoryId]
  )
  const [{ loading, data, error }, refresh] = useAPI(fetchSpecifics)

  useEffect(() => {
    if (form.categoryMarketCategoryId) {
      refresh()
    }
  }, [form.categoryMarketCategoryId, refresh])

  const setRequirements = useCallback(
    newRequirements => {
      return setGoodwillfindsRequirements(newRequirements)
    },
    [setGoodwillfindsRequirements]
  )

  useEffect(() => {
    setRequirements({ specifics: data?.specifics ?? [] })
  }, [data, setRequirements])

  const specifics: MarketplaceServiceSpecific[] = useMemo(() => {
    return goodwillfindsRequirements.specifics || []
  }, [goodwillfindsRequirements])

  const nonRequiredSpecifics: MarketplaceServiceSpecific[] = useMemo(
    () => specifics.filter(o => !o.required),
    [specifics]
  )
  const nonRequiredSpecificsCount = useMemo(() => {
    return nonRequiredSpecifics.length
  }, [nonRequiredSpecifics])
  const requiredSpecifics: MarketplaceServiceSpecific[] = useMemo(() => specifics.filter(o => o.required), [specifics])
  const requiredSpecificsSlugs: string[] = useMemo(() => requiredSpecifics.map(o => o.slug), [requiredSpecifics])

  useEffect(() => {
    if (requiredSpecifics.length === 0) {
      onListingChange({ name: 'requiredSpecificsFulfilled', value: true })
    } else {
      onListingChange({ name: 'requiredSpecificsFulfilled', value: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredSpecifics])

  const onSpecificsChange = (k, v) => {
    const newSpecificsLookup = { ...specificsLookup }
    newSpecificsLookup[k] = v
    const newListingSpecificsArray = []
    Object.keys(newSpecificsLookup).forEach(newSpecificsLookupKey => {
      newListingSpecificsArray.push({
        name: newSpecificsLookupKey,
        value: newSpecificsLookup[newSpecificsLookupKey],
        required: requiredSpecificsSlugs.includes(newSpecificsLookupKey)
      })
    })

    let completed = true
    requiredSpecifics.forEach(requiredSpecific => {
      if (
        !newListingSpecificsArray.find(specific => specific.name === requiredSpecific.slug && specific.value !== null)
      ) {
        completed = false
      }
    })

    onListingChange([
      { name: 'specifics', value: newListingSpecificsArray },
      { name: 'requiredSpecificsFulfilled', value: completed }
    ])
  }

  useEffect(() => {
    const newSpecificsLookup = {}
    const formSpecifics = form.specifics || []

    if (specifics && templateForm) {
      const slugsList = specifics.map(s => ({ name: s.slug }))

      const templateGuesses = getAutofilledSpecifics(slugsList, templateForm)
      const specificsWithOptions = specifics.filter(specific => specific.type !== 'FREE_TEXT')
      const singleOptionInputs = specificsWithOptions.reduce((acc, curr) => {
        if (!curr.required) return acc
        if (curr.valueRecommendations.length > 1) return acc

        return { ...acc, [curr.slug]: curr.valueRecommendations[0] }
      }, {})

      // Inputs are auto-filled based on A) Builder template matches and B) if they are the only option and the input is required
      const autofilledSpecifics = { ...singleOptionInputs, ...templateGuesses }

      Object.keys(autofilledSpecifics)
        .map(name => {
          const specific = specifics.find(s => s.slug === name)
          const hasValueRecommendations = (specific?.valueRecommendations?.length || 0) > 0
          const isInvalidValue = specific && !specific?.valueRecommendations?.includes(autofilledSpecifics[name])
          if (hasValueRecommendations && isInvalidValue) {
            return null
          }

          newSpecificsLookup[name] = autofilledSpecifics[name]
          return { name, value: autofilledSpecifics[name] }
        })
        .filter(s => s) // filter out any invalid recommendations we skip
    }

    formSpecifics.forEach(specific => {
      newSpecificsLookup[specific.name] = specific.value
    })

    setSpecificsLookup(newSpecificsLookup)
  }, [form, specifics, templateForm])

  useEffect(() => {
    // Validate required specifics are present
    const newLocalSpecificsErrors = {}
    requiredSpecificsSlugs.forEach(slug => {
      if (!specificsLookup[slug]) {
        newLocalSpecificsErrors[slug] = 'is required'
      }
    })

    setLocalErrors(prevLocalErrors => {
      const nextErrors = { ...prevLocalErrors }
      if (isEmpty(newLocalSpecificsErrors)) {
        delete nextErrors?.goodwillfindsListingSpecifics
      } else {
        nextErrors.goodwillfindsListingSpecifics = newLocalSpecificsErrors
      }

      return nextErrors
    })
  }, [specificsLookup, setLocalErrors, requiredSpecificsSlugs])

  return (
    <>
      <div className="stretch-12 specifics-heading" style={{ display: 'flex' }}>
        <h4 className="span-2">Item Specifics</h4>
        <Button
          text="Refresh"
          onClick={() => {
            refresh()
          }}
          loading={loading}
          minimal
          intent={Intent.PRIMARY}
        />
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          {error ? (
            // API error occurred
            <div className="stretch-12">
              <Callout intent={Intent.DANGER}>{getErrorString(error)}</Callout>
            </div>
          ) : (
            <>
              {requiredSpecifics.map(ss => {
                return (
                  <div
                    className="span-4 react-select-same-height-as-blueprint market-specifics-element-container"
                    key={ss.name}
                  >
                    {ss.type === 'FREE_TEXT' ? (
                      <SpecificsFreeTextInput
                        value={specificsLookup[ss.slug] || ''}
                        onChange={(v: string) => onSpecificsChange(ss.slug, v)}
                        specificName={ss.name}
                        error={localErrors?.goodwillfindsListingSpecifics?.[ss.slug]}
                        required
                      />
                    ) : (
                      <SpecificInput
                        required={ss.required}
                        value={specificsLookup[ss.slug] || ''}
                        error={localErrors?.goodwillfindsListingSpecifics?.[ss.slug]}
                        specificName={ss.name}
                        options={ss.valueRecommendations}
                        onChange={(v: string) => onSpecificsChange(ss.slug, v)}
                      />
                    )}
                  </div>
                )
              })}
              {nonRequiredSpecificsCount > 0 && (
                <div className="stretch-12 market-specifics-element-container">
                  {form.categoryMarketCategoryId && form.categoryMarketCategoryId && (
                    <Button
                      intent={Intent.PRIMARY}
                      minimal
                      onClick={() => {
                        analytics.track('Channel Form Section Toggle', {
                          section: 'goodwillfinds specifics section',
                          setOpen: !nonRequiredSpecificsOpen
                        })
                        setNonRequiredSpecificsOpen(!nonRequiredSpecificsOpen)
                      }}
                      className="show-more-toggle-button"
                      icon={nonRequiredSpecificsOpen ? IconNames.CARET_DOWN : IconNames.CARET_RIGHT}
                    >
                      (
                      <small>
                        {nonRequiredSpecificsOpen ? 'Hide' : 'Show'} {nonRequiredSpecificsCount} Optional Specific
                        {pluralizeFromNumber(nonRequiredSpecificsCount)}
                      </small>
                      )
                    </Button>
                  )}
                </div>
              )}
              <Collapse
                isOpen={nonRequiredSpecificsOpen}
                keepChildrenMounted
                className={nonRequiredSpecificsOpen ? 'stretch-12' : ''}
              >
                <div className="specifics-form-grid" style={specificsStyles.topPadding}>
                  {nonRequiredSpecifics.map(ss => (
                    <div
                      className="span-4 react-select-same-height-as-blueprint market-specifics-element-container"
                      key={ss.name}
                    >
                      <SpecificInput
                        required={ss.required}
                        value={form.specifics ? specificsLookup[ss.slug] : ''}
                        specificName={ss.name}
                        error={localErrors?.goodwillfindsListingSpecifics?.[ss.slug]}
                        options={ss.valueRecommendations}
                        onChange={(v: string) => onSpecificsChange(ss.slug, v)}
                      />
                    </div>
                  ))}
                </div>
              </Collapse>
            </>
          )}
        </div>
      )}
    </>
  )
}
